/* ==========================================================================
   3D Ontario — theme behaviour
   Everything here talks to real Shopify endpoints (/cart.js, /cart/add.js,
   /cart/change.js, and the Section Rendering API via ?section_id=), so no
   JavaScript needs to change when the theme is uploaded to a live store.
   ========================================================================== */

(function () {
  'use strict';

  var routes = (window.theme && window.theme.routes) || {};
  var strings = (window.theme && window.theme.strings) || {};

  // Set during init; shared so the product form can open the drawer on add.
  var cartDrawer = null;

  /* ------------------------------------------------------------ utilities */

  function $(sel, root) {
    return (root || document).querySelector(sel);
  }
  function $$(sel, root) {
    return Array.prototype.slice.call((root || document).querySelectorAll(sel));
  }

  // Formats through the shop's own money format rather than assuming a dollar
  // sign. The format string was already being passed in and ignored, which was
  // survivable while the store sold in one currency and would not be: with
  // Markets on, the server renders "US$21.00" while every price this function
  // touched -- cart drawer, variant switch, free-shipping bar -- would still
  // say "$21.00", and $ means three different things across CAD, USD and AUD.
  //
  // Shopify's format strings use {{amount}}, {{amount_with_comma_separator}}
  // and the no_decimals variants; anything unrecognised falls through to the
  // plain amount so an unusual format degrades to a number rather than blank.
  function formatMoney(cents, format) {
    var value = Number(cents) || 0;
    var pattern = format || (window.theme && window.theme.moneyFormat) || '${{amount}}';

    var group = function (num, decimals, thousands, decimal) {
      var fixed = (num / 100).toFixed(decimals);
      var parts = fixed.split('.');
      var whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return decimals && parts[1] ? whole + decimal + parts[1] : whole;
    };

    return pattern.replace(/\{\{\s*(\w+)\s*\}\}/g, function (_, name) {
      switch (name) {
        case 'amount':
          return group(value, 2, ',', '.');
        case 'amount_no_decimals':
          return group(value, 0, ',', '.');
        case 'amount_with_comma_separator':
          return group(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator':
          return group(value, 0, '.', ',');
        case 'amount_with_space_separator':
          return group(value, 2, ' ', ',');
        case 'amount_no_decimals_with_space_separator':
          return group(value, 0, ' ', ',');
        case 'amount_with_apostrophe_separator':
          return group(value, 2, "'", '.');
        default:
          return group(value, 2, ',', '.');
      }
    });
  }

  function debounce(fn, wait) {
    var t;
    return function () {
      var args = arguments;
      var ctx = this;
      clearTimeout(t);
      t = setTimeout(function () {
        fn.apply(ctx, args);
      }, wait);
    };
  }

  /** Locks background scroll while a drawer or sheet is open. */
  var scrollLock = (function () {
    var count = 0;
    var y = 0;
    return {
      on: function () {
        if (count++ > 0) return;
        y = window.scrollY;
        document.body.style.position = 'fixed';
        document.body.style.top = -y + 'px';
        document.body.style.width = '100%';
      },
      off: function () {
        if (--count > 0) return;
        count = 0;
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        window.scrollTo(0, y);
      },
    };
  })();

  /** Keeps Tab inside an open overlay. */
  function trapFocus(container, onEscape) {
    var selector =
      'a[href], button:not([disabled]), input:not([disabled]), select, textarea, summary, [tabindex]:not([tabindex="-1"])';

    function onKeydown(e) {
      if (e.key === 'Escape') {
        onEscape();
        return;
      }
      if (e.key !== 'Tab') return;
      var focusable = $$(selector, container).filter(function (el) {
        return el.offsetParent !== null;
      });
      if (!focusable.length) return;
      var first = focusable[0];
      var last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    document.addEventListener('keydown', onKeydown);
    return function () {
      document.removeEventListener('keydown', onKeydown);
    };
  }

  /* ---------------------------------------------------------------- toast */

  function toast(message) {
    var stack = $('#ToastStack');
    if (!stack) return;
    var el = document.createElement('div');
    el.className = 'toast';
    el.innerHTML =
      '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M20 6 9 17l-5-5"/></svg><span></span>';
    $('span', el).textContent = message;
    stack.appendChild(el);
    setTimeout(function () {
      el.classList.add('is-leaving');
      setTimeout(function () {
        el.remove();
      }, 260);
    }, 2600);
  }

  /* ----------------------------------------------------------------- cart */

  var Cart = {
    state: null,

    fetch: function () {
      return fetch(routes.cart_url + '.js', { headers: { Accept: 'application/json' } })
        .then(function (r) {
          return r.json();
        })
        .then(function (cart) {
          Cart.state = cart;
          Cart.render(cart);
          return cart;
        });
    },

    add: function (variantId, quantity) {
      return fetch(routes.cart_add_url + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: quantity || 1 }),
      }).then(function (r) {
        if (!r.ok) {
          return r.json().then(function (err) {
            throw new Error(err.description || 'Could not add to cart');
          });
        }
        return Cart.fetch();
      });
    },

    change: function (line, quantity) {
      return fetch(routes.cart_change_url + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line: line, quantity: quantity }),
      })
        .then(function (r) {
          return r.json();
        })
        .then(function (cart) {
          Cart.state = cart;
          Cart.render(cart);
          return cart;
        });
    },

    /** Updates the bubble, the drawer contents, and the cart page if present. */
    render: function (cart) {
      $$('[data-cart-count]').forEach(function (el) {
        el.textContent = cart.item_count;
        el.hidden = cart.item_count === 0;
      });

      var body = $('[data-cart-drawer-body]');
      if (body) body.innerHTML = Cart.itemsHTML(cart);

      var footer = $('[data-cart-drawer-footer]');
      if (footer) footer.hidden = cart.item_count === 0;

      $$('[data-cart-total]').forEach(function (el) {
        el.textContent = formatMoney(cart.total_price);
      });

      var bar = $('[data-ship-bar]');
      if (bar) {
        var threshold = Number(bar.dataset.threshold || 7500);
        var pct = Math.min(100, (cart.total_price / threshold) * 100);
        $('[data-ship-bar-fill]', bar).style.width = pct + '%';
        var remaining = Math.max(0, threshold - cart.total_price);
        $('[data-ship-bar-text]', bar).innerHTML =
          remaining > 0
            ? 'You are <strong>' + formatMoney(remaining) + '</strong> away from free shipping'
            : '<strong>Free shipping unlocked</strong> 🎉';
      }

      // The cart page is server-rendered; a quantity change there just reloads
      // so the totals and any future discounts stay authoritative.
      if (document.body.classList.contains('template-cart') && Cart._reloadCartPage) {
        Cart._reloadCartPage = false;
        window.location.reload();
      }
    },

    itemsHTML: function (cart) {
      if (!cart.items.length) {
        return (
          '<div class="empty-state" style="border:0;padding:3rem 0">' +
          '<div class="empty-state__emoji">🐉</div>' +
          '<p class="h3">Your cart is empty</p>' +
          '<p class="lede" style="margin-inline:auto">Every print is made to order. Pick a creature and we will start it this week.</p>' +
          '<p><a class="btn" href="/collections/all">Start shopping</a></p></div>'
        );
      }
      return cart.items
        .map(function (item, i) {
          return (
            '<div class="line-item" data-line="' + (i + 1) + '">' +
            '<a class="line-item__img" href="' + item.url + '"><img src="' + (item.image || '') + '" alt="" width="72" height="72"></a>' +
            '<div>' +
            '<div class="line-item__title"><a href="' + item.url + '">' + item.product_title + '</a></div>' +
            (item.variant_title ? '<div class="line-item__variant">' + item.variant_title + '</div>' : '') +
            '<div class="line-item__foot">' +
            '<div class="qty qty--sm">' +
            '<button type="button" data-qty-down aria-label="Decrease quantity"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12h14"/></svg></button>' +
            '<input type="number" value="' + item.quantity + '" min="0" data-qty-input aria-label="Quantity">' +
            '<button type="button" data-qty-up aria-label="Increase quantity"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 5v14M5 12h14"/></svg></button>' +
            '</div>' +
            '<span class="line-item__price">' + formatMoney(item.final_line_price) + '</span>' +
            '</div>' +
            '<button type="button" class="line-item__remove" data-remove>Remove</button>' +
            '</div></div>'
          );
        })
        .join('');
    },
  };

  /* -------------------------------------------------------- overlay helper */

  /**
   * Wraps a scrim + panel element. Visibility is driven by a class, and
   * `visibility: hidden` in CSS already removes the closed panel from the
   * accessibility tree — so we deliberately do not toggle aria-hidden here.
   * The facets rail uses this same component but is a permanently visible
   * sidebar on desktop, where an aria-hidden="true" left behind on close
   * would hide the whole filter UI from screen readers.
   */
  function Overlay(el, openClass) {
    if (!el) return null;
    var release = null;
    var lastFocus = null;
    var api = {
      el: el,
      open: function () {
        lastFocus = document.activeElement;
        el.classList.add(openClass);
        scrollLock.on();
        release = trapFocus(el, api.close);
        var focusTarget = el.querySelector('[data-autofocus]') || el.querySelector('button, a, input');
        if (focusTarget) setTimeout(function () { focusTarget.focus(); }, 60);
      },
      close: function () {
        el.classList.remove(openClass);
        scrollLock.off();
        if (release) release();
        release = null;
        if (lastFocus && lastFocus.focus) lastFocus.focus();
      },
      isOpen: function () {
        return el.classList.contains(openClass);
      },
    };
    // Delegated rather than bound per-button: the facets footer is re-rendered
    // on every filter change, which would discard direct listeners.
    el.addEventListener('click', function (e) {
      if (e.target.closest('[data-overlay-close]')) api.close();
    });
    return api;
  }

  /* ------------------------------------------------------------------ init */

  document.addEventListener('DOMContentLoaded', function () {
    /* --- sticky header shadow --- */
    var header = $('.header');
    if (header) {
      var onScroll = function () {
        header.classList.toggle('is-stuck', window.scrollY > 8);
      };
      onScroll();
      window.addEventListener('scroll', onScroll, { passive: true });
    }

    /* --- mobile menu --- */
    var menu = Overlay($('#MobileMenu'), 'is-open');
    if (menu) {
      $$('[data-menu-open]').forEach(function (b) {
        b.addEventListener('click', menu.open);
      });
    }

    /* --- cart drawer --- */
    var drawer = Overlay($('#CartDrawer'), 'is-open');
    var cartType = (window.theme && window.theme.cartType) || 'drawer';
    cartDrawer = drawer;

    if (drawer) {
      $$('[data-cart-open]').forEach(function (b) {
        b.addEventListener('click', function (e) {
          if (cartType !== 'drawer') return; // fall through to the cart page link
          e.preventDefault();
          drawer.open();
        });
      });

      // Quantity + remove inside the drawer (delegated: contents are re-rendered).
      drawer.el.addEventListener('click', function (e) {
        var row = e.target.closest('[data-line]');
        if (!row) return;
        var line = Number(row.dataset.line);
        var input = $('[data-qty-input]', row);
        if (e.target.closest('[data-qty-up]')) Cart.change(line, Number(input.value) + 1);
        else if (e.target.closest('[data-qty-down]')) Cart.change(line, Math.max(0, Number(input.value) - 1));
        else if (e.target.closest('[data-remove]')) Cart.change(line, 0);
      });

      drawer.el.addEventListener(
        'change',
        function (e) {
          var input = e.target.closest('[data-qty-input]');
          if (!input) return;
          var row = input.closest('[data-line]');
          Cart.change(Number(row.dataset.line), Math.max(0, Number(input.value)));
        },
        true
      );
    }

    /* --- quick add from product cards --- */
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-quick-add]');
      if (!btn) return;
      e.preventDefault();

      // Multi-variant products need a choice, so send those to the product page.
      if (btn.dataset.url && btn.dataset.needsOptions === 'true') {
        window.location.href = btn.dataset.url;
        return;
      }

      btn.classList.add('is-loading');
      Cart.add(btn.dataset.variantId, 1)
        .then(function () {
          btn.classList.remove('is-loading');
          btn.classList.add('is-done');
          toast((btn.dataset.title || 'Item') + ' added to cart');
          setTimeout(function () {
            btn.classList.remove('is-done');
          }, 1400);
          if (drawer && cartType === 'drawer') drawer.open();
        })
        .catch(function (err) {
          btn.classList.remove('is-loading');
          toast(err.message);
        });
    });

    /* --- cart page quantity controls --- */
    var cartPage = $('[data-cart-page]');
    if (cartPage) {
      cartPage.addEventListener('click', function (e) {
        var row = e.target.closest('[data-line]');
        if (!row) return;
        var line = Number(row.dataset.line);
        var input = $('[data-qty-input]', row);
        var next = null;
        if (e.target.closest('[data-qty-up]')) next = Number(input.value) + 1;
        else if (e.target.closest('[data-qty-down]')) next = Math.max(0, Number(input.value) - 1);
        else if (e.target.closest('[data-remove]')) next = 0;
        if (next === null) return;
        e.preventDefault();
        Cart._reloadCartPage = true;
        Cart.change(line, next);
      });
    }

    initFacets();
    initProductForm();
    initGallery();
    initRecommendations();
    initStickyBuy();
    initBackToTop();
    initEmailCloak();
    initContactGuard();
    initEmailCapture();
    initShare();
    initPredictiveSearch();
    initFacetHint();
    initReveal();
    initLocalization();
    initThemeToggle();

    // Sync the badge in case the page was served from bfcache.
    if ($('[data-cart-count]')) Cart.fetch();
  });

  /* ---------------------------------------------------------------- facets */

  /**
   * Filtering uses the Section Rendering API: the form's state becomes the
   * query string, we re-request the current page for just the collection
   * section, and swap in the new grid + facet counts. The URL is kept in sync
   * so filtered views are shareable and the back button works.
   */
  function initFacets() {
    var container = $('#FacetsWrapper');
    if (!container) return;

    var sheet = Overlay($('#FacetsPanel'), 'is-open');
    if (sheet) {
      $$('[data-facets-open]').forEach(function (b) {
        b.addEventListener('click', sheet.open);
      });
    }

    var sectionId = container.dataset.sectionId;
    var inFlight = null;

    /**
     * Swapping innerHTML destroys whatever the user was interacting with. Note
     * enough about the focused control to put focus (and the caret) back
     * afterwards — without this, typing in the price field loses focus the
     * moment the debounced request lands.
     */
    function captureFocus() {
      var el = document.activeElement;
      if (!el || !container.contains(el)) return null;
      return {
        name: el.name || null,
        value: el.type === 'checkbox' ? el.value : null,
        caret: el.type === 'number' || el.type === 'text' ? el.value.length : null,
      };
    }

    function restoreFocus(memo) {
      if (!memo || !memo.name) return;
      var sel = '[name="' + memo.name.replace(/"/g, '\\"') + '"]';
      if (memo.value !== null) sel += '[value="' + memo.value.replace(/"/g, '\\"') + '"]';
      var el = container.querySelector(sel);
      if (!el) return;
      el.focus({ preventScroll: true });
      if (memo.caret !== null && el.setSelectionRange) {
        // Number inputs throw on setSelectionRange in some browsers.
        try {
          el.setSelectionRange(memo.caret, memo.caret);
        } catch (err) {
          /* not selectable — focus alone is enough */
        }
      }
    }

    function apply(url, pushState) {
      var results = $('#ProductGridContainer');
      if (results) results.classList.add('is-loading');
      var focusMemo = captureFocus();

      var fetchUrl = url + (url.indexOf('?') > -1 ? '&' : '?') + 'section_id=' + encodeURIComponent(sectionId);
      if (inFlight) inFlight.abort();
      var controller = new AbortController();
      inFlight = controller;

      fetch(fetchUrl, { signal: controller.signal })
        .then(function (r) {
          return r.text();
        })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          [
            '#ProductGridContainer',
            '#FacetsForm',
            '#ActiveFacets',
            '#FacetToolbarCount',
            '#SortContainer',
            '#FacetsFooter',
          ].forEach(function (sel) {
            var next = doc.querySelector(sel);
            var current = document.querySelector(sel);
            if (next && current) current.innerHTML = next.innerHTML;
          });
          if (pushState) history.pushState({ facets: true }, '', url);
          var results2 = $('#ProductGridContainer');
          if (results2) results2.classList.remove('is-loading');
          restoreFocus(focusMemo);
          inFlight = null;
        })
        .catch(function (err) {
          if (err.name === 'AbortError') return;
          // Fall back to a normal navigation rather than leaving a stale grid.
          window.location.href = url;
        });
    }

    function urlFromForm() {
      var form = $('#FacetsForm form') || $('#FacetsForm');
      var params = new URLSearchParams();
      $$('input[type="checkbox"]:checked, input[type="number"]', form).forEach(function (input) {
        if (input.type === 'number' && !input.value) return;
        if (!input.name) return;
        params.append(input.name, input.value);
      });
      // Sort lives in the toolbar, outside the facet form — carry it along.
      var sort = $('[data-sort-select]');
      if (sort && sort.value && sort.value !== 'manual') params.set('sort_by', sort.value);
      var qs = params.toString();
      return window.location.pathname + (qs ? '?' + qs : '');
    }

    container.addEventListener('change', function (e) {
      if (e.target.matches('[data-sort-select]')) return; // handled below
      if (!e.target.matches('input[type="checkbox"]')) return;
      apply(urlFromForm(), true);
    });

    container.addEventListener(
      'input',
      debounce(function (e) {
        if (!e.target.matches('input[type="number"]')) return;
        apply(urlFromForm(), true);
      }, 550)
    );

    // Active-filter pills and "clear all" are plain links; intercept them.
    document.addEventListener('click', function (e) {
      var link = e.target.closest('[data-facet-link]');
      if (!link) return;
      e.preventDefault();
      apply(link.getAttribute('href'), true);
      if (sheet && sheet.isOpen() && link.hasAttribute('data-facet-close')) sheet.close();
    });

    // Delegated so it survives the toolbar being swapped out on each update.
    document.addEventListener('change', function (e) {
      var select = e.target.closest('[data-sort-select]');
      if (!select) return;
      var params = new URLSearchParams(window.location.search);
      params.set('sort_by', select.value);
      params.delete('page');
      apply(window.location.pathname + '?' + params.toString(), true);
    });

    window.addEventListener('popstate', function () {
      apply(window.location.pathname + window.location.search, false);
    });
  }

  /* --------------------------------------------------------- product form */

  function initProductForm() {
    var root = $('[data-product-form]');
    if (!root) return;

    var variants = JSON.parse($('[data-variants-json]', root).textContent);
    var optionCount = Number(root.dataset.optionCount || 1);

    // Price, compare-at price and the stock note sit above the <form>, so they
    // are outside `root` — scope those lookups to the whole info column.
    var info = $('[data-product-info]') || document;

    function selected() {
      var values = [];
      for (var i = 1; i <= optionCount; i++) {
        var checked = $('input[name="option' + i + '"]:checked', root);
        values.push(checked ? checked.value : null);
      }
      return values;
    }

    function matchVariant(values) {
      return variants.filter(function (v) {
        return values.every(function (val, i) {
          return val === null || v.options[i] === val;
        });
      })[0];
    }

    /** Greys out option values that produce no purchasable combination. */
    function refreshAvailability(values) {
      for (var i = 1; i <= optionCount; i++) {
        (function (index) {
          $$('input[name="option' + index + '"]', root).forEach(function (input) {
            var probe = values.slice();
            probe[index - 1] = input.value;
            var possible = variants.some(function (v) {
              return (
                v.available &&
                probe.every(function (val, k) {
                  return k === index - 1 ? v.options[k] === val : val === null || v.options[k] === val;
                })
              );
            });
            input.closest('.option-chip').classList.toggle('is-unavailable', !possible);
          });
        })(i);
      }
    }

    function update() {
      var values = selected();
      var variant = matchVariant(values);
      refreshAvailability(values);

      $$('[data-option-display]', root).forEach(function (el) {
        var idx = Number(el.dataset.optionDisplay);
        el.textContent = values[idx - 1] || '';
      });

      var submit = $('[data-add-to-cart]', root);
      var priceEl = $('[data-price]', info);
      var compareEl = $('[data-compare-price]', info);
      var stockEl = $('[data-stock-note]', info);

      if (!variant) {
        submit.disabled = true;
        submit.textContent = 'Unavailable';
        return;
      }

      $('[data-variant-id]', root).value = variant.id;
      if (priceEl) {
        priceEl.textContent = formatMoney(variant.price);
        priceEl.classList.toggle('price--sale', Boolean(variant.compare_at_price));
      }
      if (compareEl) {
        compareEl.hidden = !variant.compare_at_price;
        if (variant.compare_at_price) compareEl.textContent = formatMoney(variant.compare_at_price);
      }
      if (stockEl) {
        // This block rebuilds both the class and the text from scratch, so it
        // has to know about digital products or it silently reverts the
        // server-rendered "Instant download" to a shipping promise the moment
        // a variant is picked.
        var isDigital = stockEl.dataset.digital === 'true';
        var modifier = '';
        if (!variant.available) modifier = ' stock-note--out';
        else if (isDigital) modifier = ' stock-note--digital';
        else if (variant.inventory_quantity <= 5) modifier = ' stock-note--low';
        stockEl.className = 'stock-note' + modifier;

        // Strings come from the locale file rather than being written here.
        // The hardcoded copies this replaces had already drifted out of sync
        // with en.default.json.
        var text;
        if (!variant.available) text = strings.soldOut || 'Sold out';
        else if (isDigital) text = strings.digitalReady || 'Instant download';
        else if (variant.inventory_quantity <= 5) {
          text = (strings.lowStock || 'Only {{ count }} left').replace('{{ count }}', variant.inventory_quantity);
        } else text = strings.inStock || 'In stock';
        $('[data-stock-text]', stockEl).textContent = text;
      }

      submit.disabled = !variant.available;
      submit.textContent = variant.available ? strings.addToCart || 'Add to cart' : strings.soldOut || 'Sold out';

      // Keep the URL pointing at the chosen variant, same as Shopify does.
      var url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      history.replaceState({}, '', url);

      var mainImg = $('[data-gallery-main] img');
      if (mainImg && variant.featured_image) mainImg.src = variant.featured_image;

      // Lets the sticky buy bar mirror the buy box without reaching into it.
      document.dispatchEvent(
        new CustomEvent('variant:changed', {
          detail: { variant: variant, price: formatMoney(variant.price), available: variant.available },
        })
      );
    }

    root.addEventListener('change', function (e) {
      if (e.target.matches('input[type="radio"]')) update();
    });

    // Quantity stepper
    root.addEventListener('click', function (e) {
      var input = $('[data-qty-input]', root);
      if (!input) return;
      if (e.target.closest('[data-qty-up]')) input.value = Number(input.value) + 1;
      else if (e.target.closest('[data-qty-down]')) input.value = Math.max(1, Number(input.value) - 1);
    });

    // `root` is a div inside the {% form %} element, and submit events fire on
    // the form itself — binding here rather than on root is what stops the
    // native POST to /cart/add and keeps us on the page.
    var formEl = root.closest('form') || root;

    formEl.addEventListener('submit', function (e) {
      e.preventDefault();
      var submit = $('[data-add-to-cart]', root);
      var original = submit.textContent;
      submit.disabled = true;
      submit.textContent = 'Adding…';
      Cart.add($('[data-variant-id]', root).value, Number($('[data-qty-input]', root).value || 1))
        .then(function () {
          submit.disabled = false;
          submit.textContent = original;
          toast(strings.addedToCart || 'Added to cart');
          if (cartDrawer && (window.theme.cartType || 'drawer') === 'drawer') cartDrawer.open();
        })
        .catch(function (err) {
          submit.disabled = false;
          submit.textContent = original;
          toast(err.message);
        });
    });

    update();
  }

  /* ------------------------------------------------------- recommendations */

  /**
   * Shopify only populates `recommendations.products` when a section is
   * requested through the Product Recommendations API together with the
   * Section Rendering API — rendering the section inline yields nothing. So
   * the section ships empty and swaps itself in here.
   *
   * Deferred until the block is near the viewport: it is below the fold on
   * every product page, and there is no reason to spend a request on it while
   * someone is still reading the buy box.
   */
  function initRecommendations() {
    $$('[data-recommendations]').forEach(function (el) {
      var productId = el.dataset.productId;
      if (!productId) return;

      var load = function () {
        var url =
          '/recommendations/products?section_id=' +
          encodeURIComponent(el.dataset.sectionId) +
          '&product_id=' +
          encodeURIComponent(productId) +
          '&limit=' +
          encodeURIComponent(el.dataset.limit || 4) +
          '&intent=' +
          encodeURIComponent(el.dataset.intent || 'related');

        fetch(url)
          .then(function (r) {
            return r.text();
          })
          .then(function (html) {
            var incoming = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-recommendations]');
            // No recommendations yet is normal on a young store — leave the
            // section empty rather than showing an empty heading.
            if (incoming && incoming.innerHTML.trim()) el.innerHTML = incoming.innerHTML;
          })
          .catch(function () {
            /* recommendations are an enhancement — never block the page */
          });
      };

      if ('IntersectionObserver' in window) {
        var io = new IntersectionObserver(
          function (entries) {
            if (!entries[0].isIntersecting) return;
            io.disconnect();
            load();
          },
          { rootMargin: '600px' }
        );
        io.observe(el);
      } else {
        load();
      }
    });
  }

  /* ------------------------------------------------------- sticky buy bar */

  /**
   * Mobile add-to-cart bar. Appears once the real buy button scrolls out of
   * view, so the price and the action stay reachable through the description,
   * specs and reviews — the stretch where mobile shoppers usually drop off.
   */
  function initStickyBuy() {
    var bar = $('[data-sticky-buy]');
    var root = $('[data-product-form]');
    if (!bar || !root) return;

    // requestSubmit lives on the <form>, and root is a div inside it.
    var formEl = root.closest('form') || root;
    var anchor = $('[data-add-to-cart]', root);
    if (!anchor || !('IntersectionObserver' in window)) return;

    var io = new IntersectionObserver(
      function (entries) {
        // Show only when the real button has scrolled off the top, not when it
        // is still below the fold on first paint.
        var e = entries[0];
        var below = e.boundingClientRect.top > 0;
        bar.classList.toggle('is-visible', !e.isIntersecting && !below);
      },
      { threshold: 0 }
    );
    io.observe(anchor);

    $('[data-sticky-buy-button]', bar).addEventListener('click', function () {
      // Reuse the real form so variant selection and validation stay in one place.
      if (typeof formEl.requestSubmit === 'function') formEl.requestSubmit();
      else anchor.click();
    });

    // Keep the bar's price and button state in step with the variant picker.
    document.addEventListener('variant:changed', function (e) {
      var priceEl = $('[data-sticky-buy-price]', bar);
      if (priceEl) priceEl.textContent = e.detail.price;
      var btn = $('[data-sticky-buy-button]', bar);
      btn.disabled = !e.detail.available;
      btn.textContent = e.detail.available ? strings.addToCart || 'Add to cart' : strings.soldOut || 'Sold out';
    });
  }

  /* ----------------------------------------------------------- back to top */

  /**
   * Shows the back-to-top control once the page is scrolled far enough to be
   * worth it, and drives the progress ring from scroll position.
   *
   * Scroll work is throttled through requestAnimationFrame — a bare scroll
   * listener that writes to the DOM fires far more often than the screen
   * refreshes and makes momentum scrolling stutter on phones, which is exactly
   * the case this button exists to help with.
   */
  function initBackToTop() {
    var btn = $('[data-to-top]');
    if (!btn) return;

    var ring = $('[data-to-top-ring]', btn);
    var CIRCUMFERENCE = 2 * Math.PI * 21;
    var SHOW_AFTER = 600;
    var ticking = false;

    function paint() {
      ticking = false;
      var y = window.scrollY || document.documentElement.scrollTop;
      var max = document.documentElement.scrollHeight - window.innerHeight;
      var pct = max > 0 ? Math.min(1, Math.max(0, y / max)) : 0;

      btn.classList.toggle('is-visible', y > SHOW_AFTER);
      if (ring) ring.style.strokeDashoffset = String(CIRCUMFERENCE * (1 - pct));
    }

    function onScroll() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(paint);
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    paint();

    btn.addEventListener('click', function () {
      var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      window.scrollTo({ top: 0, behavior: reduced ? 'auto' : 'smooth' });

      // Return focus to the top of the document, or a keyboard user is left
      // stranded at the bottom of the page after the scroll finishes.
      var target = document.getElementById('MainContent') || document.body;
      target.focus({ preventScroll: true });
    });
  }

  /* ------------------------------------------------------------ light/dark */

  // The attribute is already set by the inline script in <head>, before first
  // paint. This only wires the button and keeps the choice.
  function initThemeToggle() {
    var root = document.documentElement;
    var buttons = $$('[data-theme-toggle]');
    if (!buttons.length) return;

    var meta = $('[data-theme-color]');
    // Read back from the stylesheet rather than hardcoding, so the colours
    // stay in one place and follow any theme-setting change.
    var barColour = function () {
      return getComputedStyle(root).getPropertyValue('--paper').trim();
    };

    var sync = function (mode) {
      buttons.forEach(function (b) {
        // role="switch" pairs with aria-checked, not aria-pressed.
        b.setAttribute('aria-checked', String(mode === 'dark'));
      });
      // Keeps the browser chrome on a phone matching the page instead of
      // leaving a pale bar above a dark site.
      if (meta) meta.setAttribute('content', barColour());
    };

    sync(root.getAttribute('data-theme') || 'light');

    buttons.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var next = root.getAttribute('data-theme') === 'dark' ? 'light' : 'dark';
        root.setAttribute('data-theme', next);
        try {
          localStorage.setItem('kiln:theme', next);
        } catch (e) {
          /* Choice will not persist, but the page still switches. */
        }
        sync(next);
      });
    });

    // Follow the OS only while the visitor has not expressed a preference of
    // their own; once they have chosen, their choice sticks.
    if (window.matchMedia) {
      var mq = window.matchMedia('(prefers-color-scheme: dark)');
      var onChange = function (e) {
        var saved = null;
        try {
          saved = localStorage.getItem('kiln:theme');
        } catch (err) {
          /* treated as no preference */
        }
        if (saved) return;
        var mode = e.matches ? 'dark' : 'light';
        root.setAttribute('data-theme', mode);
        sync(mode);
      };
      if (mq.addEventListener) mq.addEventListener('change', onChange);
      else if (mq.addListener) mq.addListener(onChange);
    }
  }

  /* ---------------------------------------------------------- localization */

  // Submits on change so choosing a country is one action rather than two.
  // The submit button stays in the markup and is hidden by CSS only when
  // scripting is available, so a no-JS visitor can still change currency.
  function initLocalization() {
    $$('[data-locale-select]').forEach(function (select) {
      select.addEventListener('change', function () {
        var form = select.closest('form');
        if (form) form.submit();
      });
    });
  }

  /* ------------------------------------------------------------ reduced motion */

  // Checked once and reused. Anything decorative must ask before animating:
  // for some people motion causes nausea or migraine, and the OS setting is
  // how they say so.
  var prefersReducedMotion =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* --------------------------------------------------------- filter coach mark */

  // Shown once, ever. A hint that reappears is an advert, and an advert next
  // to a control makes that control feel like something to avoid.
  function initFacetHint() {
    var btn = $('[data-facets-hint]');
    if (!btn) return;

    // Desktop shows the filters as a permanent sidebar, so there is nothing
    // to point at.
    if (!window.matchMedia('(max-width: 999px)').matches) return;

    var KEY = 'kiln:facet-hint-seen';
    try {
      if (localStorage.getItem(KEY)) return;
    } catch (e) {
      // Private browsing can throw on access. Better to skip the hint than
      // to break the page for the sake of one.
      return;
    }

    var hint = document.createElement('div');
    hint.className = 'facet-hint';
    hint.setAttribute('role', 'note');
    hint.innerHTML =
      '<span class="facet-hint__arrow" aria-hidden="true"></span>' +
      '<span>Filter by creature, age or price in here</span>' +
      '<button type="button" class="facet-hint__close" aria-label="Dismiss">&times;</button>';

    var dismiss = function () {
      hint.classList.remove('is-in');
      try {
        localStorage.setItem(KEY, '1');
      } catch (e) {
        /* nothing to do */
      }
      setTimeout(function () {
        if (hint.parentNode) hint.parentNode.removeChild(hint);
      }, prefersReducedMotion ? 0 : 260);
    };

    var wrap = btn.parentNode;
    if (getComputedStyle(wrap).position === 'static') wrap.style.position = 'relative';
    wrap.appendChild(hint);

    // Aim it at the button rather than at the corner of the row. The filter
    // button is not the last control in that row, so a hint pinned right
    // points at the sort dropdown instead — precisely the wrong thing.
    requestAnimationFrame(function () {
      var wrapBox = wrap.getBoundingClientRect();
      var btnBox = btn.getBoundingClientRect();
      var hintW = hint.offsetWidth;

      var left = btnBox.left - wrapBox.left;
      // Keep it inside the row if the button sits close to the right edge.
      var maxLeft = wrap.clientWidth - hintW;
      if (left > maxLeft) left = Math.max(0, maxLeft);

      hint.style.left = left + 'px';
      hint.style.right = 'auto';

      // Arrow over the middle of the button, clamped so it stays on the card.
      var arrow = hint.querySelector('.facet-hint__arrow');
      var arrowLeft = btnBox.left - wrapBox.left - left + btnBox.width / 2 - 5;
      arrow.style.left = Math.min(Math.max(arrowLeft, 10), hintW - 20) + 'px';
      arrow.style.right = 'auto';

      hint.classList.add('is-in');
    });

    hint.querySelector('.facet-hint__close').addEventListener('click', dismiss);
    // Pressing the button is proof the hint worked; it should not linger.
    btn.addEventListener('click', dismiss, { once: true });
    // Never outstays its welcome, even if ignored.
    setTimeout(dismiss, 6000);
  }

  /* ------------------------------------------------------------- scroll reveal */

  // IntersectionObserver, not a scroll listener: the browser does the work off
  // the main thread and there is no per-frame callback to throttle.
  //
  // Elements are visible by default in CSS and only hidden once `.js` is on
  // the document, so a failure here — or no JavaScript at all — leaves the
  // page fully readable rather than blank.
  function initReveal() {
    var targets = $$('[data-reveal]');
    if (!targets.length) return;

    if (prefersReducedMotion || !('IntersectionObserver' in window)) {
      targets.forEach(function (el) {
        el.classList.add('is-revealed');
      });
      return;
    }

    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-revealed');
          // One-shot: re-animating on every scroll past is the thing that
          // makes these feel cheap, and it keeps the observer list shrinking.
          io.unobserve(entry.target);
        });
      },
      // Fires slightly before the element reaches the viewport, so by the
      // time it is actually on screen the animation has begun rather than
      // starting late and drawing attention to itself.
      { rootMargin: '0px 0px -12% 0px', threshold: 0 }
    );

    targets.forEach(function (el) {
      io.observe(el);
    });
  }

  /* ------------------------------------------------------ predictive search */

  function initPredictiveSearch() {
    $$('[data-psearch]').forEach(function (root) {
      var input = $('[data-psearch-input]', root);
      var panel = $('[data-psearch-panel]', root);
      var status = $('[data-psearch-status]', root);
      var clear = $('[data-psearch-clear]', root);
      var form = root.querySelector('form');
      if (!input || !panel) return;

      var MIN = 2;
      var timer = null;
      // Every request carries a sequence number and only the newest is allowed
      // to render. Without this, a slow response for "dra" can land after the
      // fast one for "dragon" and replace correct results with stale ones.
      var seq = 0;
      var active = -1;

      var close = function () {
        panel.hidden = true;
        input.setAttribute('aria-expanded', 'false');
        input.removeAttribute('aria-activedescendant');
        active = -1;
      };

      var items = function () {
        return $$('[data-psearch-item]', panel);
      };

      var highlight = function (i) {
        var list = items();
        if (!list.length) return;
        // Wraps, so arrowing past the end returns to the top rather than
        // dead-ending.
        if (i < 0) i = list.length - 1;
        if (i >= list.length) i = 0;
        active = i;
        list.forEach(function (el, n) {
          var on = n === i;
          el.setAttribute('aria-selected', String(on));
          el.classList.toggle('is-active', on);
          if (on) {
            input.setAttribute('aria-activedescendant', el.id);
            // `nearest` keeps the panel still when the item is already
            // visible; centring on every keypress makes the list lurch.
            el.scrollIntoView({ block: 'nearest' });
          }
        });
      };

      var render = function (html, term) {
        panel.innerHTML = html;
        var count = items().length;
        panel.hidden = count === 0 && html.indexOf('psearch__empty') === -1;
        input.setAttribute('aria-expanded', String(!panel.hidden));
        active = -1;
        input.removeAttribute('aria-activedescendant');
        if (status) {
          status.textContent = count
            ? count + ' results for ' + term
            : 'No results for ' + term;
        }
      };

      var run = function (term) {
        var mine = ++seq;
        var url =
          '/search/suggest?q=' +
          encodeURIComponent(term) +
          '&section_id=predictive-search' +
          '&resources[type]=product,collection,query&resources[limit]=6';

        fetch(url)
          .then(function (r) {
            return r.ok ? r.text() : Promise.reject(new Error(r.status));
          })
          .then(function (html) {
            if (mine !== seq) return;
            render(html, term);
          })
          .catch(function () {
            // Silent: the form still submits on Enter, so a failed suggest
            // costs the shopper nothing but a dropdown.
            if (mine === seq) close();
          });
      };

      input.addEventListener('input', function () {
        var term = input.value.trim();
        if (clear) clear.hidden = term === '';
        clearTimeout(timer);

        if (term.length < MIN) {
          close();
          return;
        }
        // 200ms: below roughly 150 a fast typist fires a request per
        // keystroke, above 300 it stops feeling live.
        timer = setTimeout(function () {
          run(term);
        }, 200);
      });

      input.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') {
          close();
          return;
        }
        if (panel.hidden) return;

        if (e.key === 'ArrowDown') {
          e.preventDefault();
          highlight(active + 1);
        } else if (e.key === 'ArrowUp') {
          e.preventDefault();
          highlight(active - 1);
        } else if (e.key === 'Enter' && active > -1) {
          // Only hijack Enter when something is highlighted; otherwise the
          // form submits to the full search page as it always did.
          var el = items()[active];
          if (el && el.dataset.url) {
            e.preventDefault();
            window.location.href = el.dataset.url;
          }
        }
      });

      panel.addEventListener('click', function (e) {
        var item = e.target.closest('[data-psearch-item]');
        if (item && item.dataset.url) window.location.href = item.dataset.url;
      });

      panel.addEventListener('mousemove', function (e) {
        var item = e.target.closest('[data-psearch-item]');
        if (item) highlight(items().indexOf(item));
      });

      if (clear) {
        clear.addEventListener('click', function () {
          input.value = '';
          clear.hidden = true;
          close();
          input.focus();
        });
      }

      document.addEventListener('click', function (e) {
        if (!root.contains(e.target)) close();
      });

      // Reopen on focus if there is already a term, so returning to the field
      // does not require retyping.
      input.addEventListener('focus', function () {
        if (input.value.trim().length >= MIN && panel.innerHTML.trim()) {
          panel.hidden = false;
          input.setAttribute('aria-expanded', 'true');
        }
      });

      if (form) {
        form.addEventListener('submit', close);
      }
    });
  }

  /* --------------------------------------------------------- email capture */

  // Disclosure only. Nothing here opens a panel by itself — no timer, no
  // scroll trigger, no exit intent. The one exception is a form that came back
  // from Shopify with a result, which is reopened so the person sees what
  // happened to what they just submitted.
  function initEmailCapture() {
    $$('[data-capture]').forEach(function (root) {
      var toggle = $('[data-capture-toggle]', root);
      var panel = $('[data-capture-panel]', root);
      if (!toggle || !panel) return;

      var open = function () {
        panel.hidden = false;
        toggle.setAttribute('aria-expanded', 'true');
        root.classList.add('is-open');
      };

      // A success or error message is useless behind a collapsed panel.
      if ($('.capture__success', panel) || $('.capture__error', panel)) {
        open();
        return;
      }

      toggle.addEventListener('click', function () {
        if (panel.hidden) {
          open();
          var field = $('input[type="email"]', panel);
          if (field) field.focus();
        } else {
          panel.hidden = true;
          toggle.setAttribute('aria-expanded', 'false');
          root.classList.remove('is-open');
        }
      });
    });
  }

  /* ----------------------------------------------------------------- share */

  // Uses the OS share sheet where there is one, which on a phone is the
  // fastest route to Facebook and puts no third-party script on the page. The
  // plain links stay in the markup as the fallback, so this only ever removes
  // a step rather than being the only way through.
  function initShare() {
    if (!navigator.share) return;

    $$('[data-share-native]').forEach(function (btn) {
      btn.hidden = false;
      btn.addEventListener('click', function () {
        navigator
          .share({
            title: btn.dataset.shareTitle || document.title,
            url: btn.dataset.shareUrl || window.location.href,
          })
          .catch(function () {
            /* Dismissing the sheet rejects; that is not an error. */
          });
      });
    });
  }

  /* ----------------------------------------------------------- email cloak */

  // Joins the address that email-cloak.liquid deliberately left in pieces, so
  // the served HTML never contains a harvestable `user@host` or `mailto:`.
  function initEmailCloak() {
    $$('.email-cloak').forEach(function (el) {
      var user = el.dataset.emailUser;
      var domain = el.dataset.emailDomain;
      if (!user || !domain) return;

      var address = user + '@' + domain;

      if (el.dataset.emailPlain === 'true') {
        el.textContent = address;
        return;
      }

      var a = document.createElement('a');
      a.href = 'mail' + 'to:' + address;
      a.textContent = address;
      el.appendChild(a);
    });
  }

  /* --------------------------------------------------------- contact guard */

  // Two cheap checks on the contact form. Neither is a substitute for
  // Shopify's spam protection, which is the only layer that sees a bot posting
  // straight to /contact — these only catch bots that actually load the page.
  function initContactGuard() {
    var form = document.getElementById('ContactForm');
    if (!form) return;

    var honeypot = form.querySelector('[name="message_ref"]');
    var token = form.querySelector('[data-human-check]');
    var message = form.querySelector('[data-guard-message]');
    var loadedAt = Date.now();

    // Stamped on real interaction rather than on load, so simply executing the
    // page's scripts is not enough to earn the token.
    var stamp = function () {
      if (token && !token.value) token.value = 'passed';
    };
    ['pointerdown', 'keydown', 'focusin'].forEach(function (evt) {
      form.addEventListener(evt, stamp, { once: true, passive: true });
    });

    form.addEventListener('submit', function (e) {
      var filledTrap = honeypot && honeypot.value !== '';
      // A person cannot read the fields, think, and type in under three
      // seconds. Scripted fills routinely submit in well under one.
      var tooFast = Date.now() - loadedAt < 3000;

      if (!filledTrap && !tooFast) return;

      e.preventDefault();
      if (message) {
        message.hidden = false;
        message.focus();
      }
    });
  }

  /* --------------------------------------------------------------- gallery */

  function initGallery() {
    var gallery = $('[data-gallery]');
    if (!gallery) return;

    var stage = $('[data-gallery-stage]', gallery);
    if (!stage) return;

    var items = $$('[data-gallery-item]', stage);
    var thumbs = $$('[data-gallery-thumb]', gallery);
    if (!items.length) return;

    // Playing media must stop when it leaves the stage, or a video keeps
    // talking from behind a 3D model the shopper is now looking at.
    var quiet = function (item) {
      var video = $('video', item);
      if (video && !video.paused) video.pause();

      // An external embed is torn down rather than paused: a YouTube iframe
      // carries on running, and cookies with it, whether or not it is on
      // screen.
      var host = $('[data-external-video]', item);
      if (host) {
        var frame = $('iframe', host);
        if (frame) frame.remove();
        var poster = $('.gallery__external-poster', host);
        var play = $('[data-external-play]', host);
        if (poster) poster.hidden = false;
        if (play) play.hidden = false;
      }
    };

    var show = function (id, scroll) {
      items.forEach(function (item) {
        var on = item.dataset.mediaId === id;
        item.classList.toggle('is-active', on);
        // aria-hidden rather than display:none so a model-viewer that is
        // already initialised is not torn down and rebuilt on every press.
        if (on) {
          item.removeAttribute('aria-hidden');
        } else {
          item.setAttribute('aria-hidden', 'true');
          quiet(item);
        }
      });

      thumbs.forEach(function (t) {
        t.setAttribute('aria-current', String(t.dataset.mediaId === id));
      });

      if (scroll) {
        var target = items.filter(function (i) {
          return i.dataset.mediaId === id;
        })[0];
        // Only meaningful while the stage is a scroll-snap track, which is
        // the phone layout; on desktop the track does not overflow so this
        // is a no-op.
        if (target) stage.scrollTo({ left: target.offsetLeft, behavior: prefersReducedMotion ? 'auto' : 'smooth' });
      }
    };

    thumbs.forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        show(thumb.dataset.mediaId, true);
      });
    });

    // Swiping the stage is the primary gesture on a phone, so the thumbnails
    // follow the stage rather than only driving it.
    var syncing = null;
    stage.addEventListener(
      'scroll',
      function () {
        clearTimeout(syncing);
        syncing = setTimeout(function () {
          var mid = stage.scrollLeft + stage.clientWidth / 2;
          var current = items[0];
          items.forEach(function (item) {
            if (item.offsetLeft <= mid) current = item;
          });
          if (current && !current.classList.contains('is-active')) {
            show(current.dataset.mediaId, false);
          }
        }, 90);
      },
      { passive: true }
    );

    // External embeds are built on demand: until someone presses play there
    // is no third-party iframe on the page at all.
    $$('[data-external-play]', gallery).forEach(function (btn) {
      btn.addEventListener('click', function () {
        var host = btn.closest('[data-external-video]');
        var tpl = $('[data-external-embed]', host);
        if (!host || !tpl || $('iframe', host)) return;
        host.appendChild(tpl.content.cloneNode(true));
        var poster = $('.gallery__external-poster', host);
        if (poster) poster.hidden = true;
        btn.hidden = true;
      });
    });
  }
})();
